import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Plug, ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight,
  ShieldCheck, Info, ChevronRight, Lock,
} from "lucide-react";
import SectionHeader from "../components/ui/SectionHeader";
import MetricCard from "../components/ui/MetricCard";
import { useTranslate } from "../i18n/useTranslate";
import { usePreferences } from "../context/PreferencesContext";
import {
  KUMBHATHON_CONTEXT, INTEGRATION_SLOTS, INTEGRATION_PRINCIPLES,
  INTEGRATION_STATUS, integrationSummary,
} from "../data/kumbhathon";
import { useIntelligence } from "../intelligence/useIntelligence";

/* ──────────────────────────────────────────────────────────────────────────
 * Kumbhathon ecosystem integration.
 *
 * This page is deliberately not a partner logo wall. It names no company,
 * because placing an organisation's name beside an "integration" panel
 * implies a relationship that does not exist — and a screenshot of that
 * outlives any caveat underneath it.
 *
 * What it shows instead is the integration surface: eight capability slots,
 * each one derived from a gap the platform reports about ITSELF in its own
 * assurance layer, with the interface contract a provider would build
 * against. That is the more useful artefact for a startup in the room, and
 * it is the only version that is true.
 *
 * The gap counts at the top are read live from the assurance engine rather
 * than typed in, so this page cannot drift away from what the platform
 * actually knows about its own blind spots.
 * ────────────────────────────────────────────────────────────────────────── */

const STATUS_STYLE = {
  [INTEGRATION_STATUS.UNCONNECTED]: { cls: "bg-slate-100 text-slate-600 border-slate-300", label: "Unconnected" },
  [INTEGRATION_STATUS.SPEC_READY]:  { cls: "bg-sky-100 text-sky-800 border-sky-300",       label: "Spec ready" },
  [INTEGRATION_STATUS.PILOT]:       { cls: "bg-amber-100 text-amber-800 border-amber-300", label: "Pilot" },
  [INTEGRATION_STATUS.LIVE]:        { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "Live" },
};

const DIRECTION_ICON = {
  inbound: ArrowDownToLine,
  outbound: ArrowUpFromLine,
  bidirectional: ArrowLeftRight,
};

export default function KumbhathonIntegration() {
  const t = useTranslate();
  const { lang } = usePreferences();
  const { assurance, hazards } = useIntelligence({ running: false });
  const summary = useMemo(() => integrationSummary(), []);
  const [open, setOpen] = useState(null);

  const L = (o) => (typeof o === "string" ? o : o?.[lang] ?? o?.en ?? "");

  /* Read live from the assurance layer, so the case for integrating is made
   * by the platform's own findings rather than by this page's copy. */
  const absentFeeds = assurance.dataAvailability.absent;
  const blindHazards = hazards.coverage.blind;

  return (
    <div className="space-y-8">
      <SectionHeader
        icon="Plug"
        title="Kumbhathon Ecosystem Integration"
        subtitle="Capability slots · interface contracts · data-sharing principles · integration status"
      />

      {/* The disclaimer sits FIRST and is not collapsible. Anything less
          prominent would be scrolled past, and this is the one claim on the
          page that would do real damage if it were misread. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5"
      >
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-amber-100">
            <Info size={22} className="text-amber-700" />
          </div>
          <div>
            <h3 className="mb-1 text-sm font-black text-slate-900">
              {t("No partnership exists")}
            </h3>
            <p className="max-w-4xl text-xs leading-relaxed text-slate-700">
              {t(KUMBHATHON_CONTEXT.status)}
            </p>
            <p className="mt-2 max-w-4xl text-xs leading-relaxed text-slate-600">
              {t(KUMBHATHON_CONTEXT.what)}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Counts read from the engines, not typed. */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <MetricCard title="Capability Slots"   value={String(summary.total)}       icon="Plug"        index={0} />
        <MetricCard title="Live Integrations"  value={String(summary.live)}        icon="CheckCircle" index={1} />
        <MetricCard title="Unconnected Feeds"  value={String(absentFeeds)}         icon="Unplug"      index={2} />
        <MetricCard title="Unmonitored Hazards" value={String(blindHazards)}       icon="EyeOff"      index={3} />
      </div>

      {/* Why integrate — argued from the platform's own findings. */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-2 text-sm font-black text-slate-900">
          {t("Why these slots exist")}
        </h3>
        <p className="mb-3 max-w-4xl text-xs leading-relaxed text-slate-600">
          {t(KUMBHATHON_CONTEXT.why)}
        </p>
        <div className="space-y-1.5">
          {assurance.findings.slice(0, 4).map((f) => (
            <div key={f.text} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <span className={`mt-0.5 rounded px-1.5 py-0.5 text-[9px] font-black uppercase ${
                f.severity === "critical" ? "bg-red-100 text-red-700"
                : f.severity === "high" ? "bg-orange-100 text-orange-700"
                : "bg-amber-100 text-amber-700"
              }`}>
                {f.severity}
              </span>
              <p className="text-xs leading-snug text-slate-700">{f.text}</p>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] italic text-slate-500">
          {t("Read live from the platform's own assurance layer. The case for integrating is the platform's own finding, not this page's copy.")}
        </p>
      </div>

      {/* The slots. */}
      <div>
        <h3 className="mb-3 text-sm font-black text-slate-900">
          {t("Integration surface")}
        </h3>
        <div className="space-y-2">
          {INTEGRATION_SLOTS.map((slot, i) => {
            const S = STATUS_STYLE[slot.status];
            const DirIcon = DIRECTION_ICON[slot.contract.direction];
            const expanded = open === slot.id;
            return (
              <motion.div
                key={slot.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
              >
                <button
                  type="button"
                  onClick={() => setOpen(expanded ? null : slot.id)}
                  className="flex w-full items-start gap-3 p-4 text-left hover:bg-slate-50"
                >
                  <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100">
                    <DirIcon size={16} className="text-slate-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-black text-slate-900">{L(slot.capability)}</span>
                      <span className={`rounded border px-1.5 py-0.5 text-[9px] font-black uppercase ${S.cls}`}>
                        {t(S.label)}
                      </span>
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold text-slate-600">
                        {slot.section}
                      </span>
                    </div>
                    <p className="mt-1 text-xs leading-snug text-slate-600">
                      <strong className="text-slate-700">{t("Gap")}:</strong> {t(slot.fillsGap)}
                    </p>
                  </div>
                  <ChevronRight
                    size={16}
                    className={`mt-1 flex-shrink-0 text-slate-400 transition-transform ${expanded ? "rotate-90" : ""}`}
                  />
                </button>

                {expanded && (
                  <div className="border-t border-slate-200 bg-slate-50 p-4">
                    <p className="mb-3 text-xs leading-relaxed text-slate-700">{t(slot.approach)}</p>

                    {/* The contract — the reason a developer would read
                        this page at all. */}
                    <div className="mb-3 rounded-xl border border-slate-300 bg-white p-3">
                      <div className="mb-1.5 text-[10px] font-black uppercase tracking-wider text-slate-400">
                        {t("Interface contract")}
                      </div>
                      <dl className="space-y-1 text-xs">
                        {[
                          ["Direction", slot.contract.direction],
                          ["Endpoint", slot.contract.endpoint],
                          ["Payload", slot.contract.payload],
                          ["Cadence", slot.contract.cadence],
                          ["Classification", slot.contract.classification],
                        ].map(([k, v]) => (
                          <div key={k} className="flex gap-2">
                            <dt className="w-24 flex-shrink-0 font-bold text-slate-500">{t(k)}</dt>
                            <dd className="flex-1 break-words font-mono text-[11px] text-slate-800">{v}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>

                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                      <div className="mb-1 text-[10px] font-black uppercase tracking-wider text-emerald-700">
                        {t("What this unlocks")}
                      </div>
                      <ul className="space-y-0.5">
                        {slot.unlocks.map((u) => (
                          <li key={u} className="text-xs leading-snug text-emerald-900">· {u}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Principles — each tied to an enforced control, not a promise. */}
      <div className="rounded-2xl border border-violet-200 bg-gradient-to-r from-violet-50 via-indigo-50 to-blue-50 p-5">
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-violet-100">
            <ShieldCheck size={20} className="text-violet-700" />
          </div>
          <div>
            <h3 className="text-sm font-black text-slate-900">
              {t("Conditions any integration must satisfy")}
            </h3>
            <p className="text-xs text-slate-600">
              {t("Each is enforced in code, not stated as an intention. The module that enforces it is named.")}
            </p>
          </div>
        </div>
        <div className="grid gap-2 md:grid-cols-2">
          {INTEGRATION_PRINCIPLES.map((p) => (
            <div key={p.enforcedBy} className="rounded-xl border border-violet-200 bg-white p-3">
              <div className="mb-1 flex items-center gap-1.5">
                <Lock size={11} className="text-violet-600" />
                <span className="text-xs font-black text-slate-900">{L(p.principle)}</span>
              </div>
              <p className="mb-1.5 text-[11px] leading-snug text-slate-600">{t(p.detail)}</p>
              <code className="text-[10px] text-violet-700">{p.enforcedBy}</code>
            </div>
          ))}
        </div>
      </div>

      <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-500">
        <Plug size={12} className="mt-0.5 flex-shrink-0" />
        {t(summary.statement)}
      </p>
    </div>
  );
}
